import React, { useState } from 'react';
import { getArtisManifest, fetchStoredDoc, checkArtisDocs } from '../../truckmateStore';
import { artisForStop, isBrokerLoad } from './model';
import { IcClip, IcDoc } from './cardIcons';

// Open a stored document in a new tab with the normal login (never a public link). The tab opens on the
// click (so it isn't blocked as a pop-up), then gets the file.
export async function openStoredDoc(id, getId = null) {
  const w = window.open('', '_blank');
  try {
    const docId = getId ? await getId() : id;
    const d = await fetchStoredDoc(docId);
    if (w) w.location.href = d.url; else window.location.href = d.url;
  } catch (e) { if (w) w.close(); throw e; }
}

const local = (txt) => String(txt || '').replace(/\s*Local Time$/i, ' local');

// On a stop: ARTIS's own ETA (read twice a day) beside ours, and the customer's Trip Manifest PDF.
// part: 'eta' / 'docs' shows only that half (the stop card puts them under Timing and Documents).
export default function ArtisStop({ s, trip, oursMs = null, onError, onChanged = null, part = null, heading = null }) {
  const a = artisForStop(s, trip);
  const [busy, setBusy] = useState('');
  const [found, setFound] = useState(null);   // ARTIS documents for this stop's bills (checked on request)
  const bills = (s.bills || []).filter(Boolean);
  // no ARTIS customer number on this stop → the server looks the customer up by name + city
  const place = String(s.place || '').split(',').map((x) => x.trim());
  const broker = isBrokerLoad(trip);   // broker loads: ARTIS documents (POD / BOL / rate con) only — no trip manifests
  const byName = !broker && !a.customers.length && s.customer ? { customer: s.customer, city: place[0] || '', state: (place[1] || '').slice(0, 2) } : null;
  if (!a.customers.length && !a.eta && !bills.length && !byName) return null;
  const checkDocs = async () => {
    setBusy('docs');
    try { const r = await checkArtisDocs(trip.tripNumber, bills); setFound(r.documents || []); if (onChanged) onChanged(); }
    catch (err) { onError(err.message || 'Could not check Synergize documents.'); } finally { setBusy(''); }
  };
  const e = a.eta;
  const diffH = e && e.etaMs && oursMs ? Math.round(((e.etaMs - oursMs) / 3600000) * 10) / 10 : null;
  const open = async (c) => {
    setBusy(c.clientId || 'name');
    try { await openStoredDoc(null, async () => (await getArtisManifest(trip.tripNumber, c.clientId || null, 'detail', c.clientId ? null : byName)).id); }
    catch (err) { onError(err.message || 'Could not get the manifest from ARTIS.'); } finally { setBusy(''); }
  };
  const showEta = part !== 'docs';
  const showDocs = part !== 'eta';
  if (part === 'eta' && !e) return null;
  if (part === 'docs' && !((!broker && a.customers.length > 0) || bills.length > 0 || byName)) return null;
  return (
    <div className="tmw-artis">
      {heading}
      {showEta && e && !e.delivered && e.etaText && (
        <div className="tmw-meta">ARTIS ETA <b>{local(e.etaText)}</b>{e.miles != null ? ` · ${e.miles.toLocaleString()} mi` : ''}{e.location ? ` · at ${e.location}` : ''}
          {diffH != null && Math.abs(diffH) >= 2 && <span className="tmw-artis-diff"> · {Math.abs(diffH)} h {diffH > 0 ? 'later' : 'earlier'} than ours</span>}
          {e.readAt && <span className="tmw-small"> · read {new Date(e.readAt).toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} ET</span>}
        </div>
      )}
      {showEta && e && e.delivered && <div className="tmw-meta">ARTIS: delivered{e.arrivedAt ? ` ${e.arrivedAt}` : ''}</div>}
      {showDocs && ((!broker && a.customers.length > 0) || bills.length > 0 || byName) && (
        <div className="tmw-artis-docs">
          {bills.length > 0 && <button type="button" className="tmw-btn tmw-btn-sm" disabled={!!busy} onClick={checkDocs} title="POD / BOL / invoice in Synergize for this stop's bills">{busy === 'docs' ? `Checking ${bills.length} bill${bills.length === 1 ? '' : 's'}…` : <><IcClip /> Synergize documents</>}</button>}
          {byName && <button type="button" className="tmw-btn tmw-btn-sm" disabled={!!busy} onClick={() => open({ clientId: null })} title="The Trip Manifest Detail from ARTIS — farms, products, POs, boxes and cubes for this customer">{busy === 'name' ? 'Getting it from ARTIS…' : <><IcDoc /> Trip manifest</>}</button>}
          {!broker && a.customers.slice(0, 3).map((c) => (
            <button key={c.clientId} type="button" className="tmw-btn tmw-btn-sm" disabled={!!busy} onClick={() => open(c)} title="The Trip Manifest Detail from ARTIS — farms, products, POs, boxes and cubes for this customer">
              {busy === c.clientId ? 'Getting it from ARTIS…' : <><IcDoc /> {`Trip manifest${a.customers.length > 1 ? ` · ${c.name}` : ''}`}</>}
            </button>
          ))}
        </div>
      )}
      {showDocs && found && (found.length
        ? <div className="tmw-artis-docs">{found.map((d) => (d.docId
          ? <button key={d.artisId || d.docId} type="button" className="tmw-btn tmw-btn-sm" onClick={() => openStoredDoc(d.docId).catch((e) => onError(e.message))}><IcDoc /> {d.type} · {d.bill}</button>
          : <span key={d.artisId} className="tmw-small">{d.type} · {d.bill} — {d.note ? d.note.replace(/ARTIS/g, 'Synergize') : 'open it in Synergize'}</span>))}</div>
        : <div className="tmw-small">No POD / BOL in Synergize for these bills yet.</div>)}
    </div>
  );
}
